/**
 * Events CSV import (gun shows + range nights): the column spec, the template / example files, and the
 * browser-side parse + normalise + per-column validation.
 *
 * The browser turns what a person types in a spreadsheet (1/31/2027, "9:00 AM", "Central", "Texas", a ZIP that
 * Excel stripped to 4 digits) into one strict shape. The database (migration 980, event_csv_import_plan) then
 * re-validates that shape, resolves the organization, fills the venue from the host, finds duplicates and
 * creates the event. The server is authoritative; the checks here exist so most mistakes show instantly with
 * the column name, before anything is sent.
 */
import Papa from "papaparse";
import { normalizeZip5, resolveEventTimezoneByZip } from "./zipTimezone";

export type ImportEventType = "gun_show" | "range_night";

export interface EventCsvColumn {
  key: string;
  /** "always" = every row; "gun_show" = required for gun shows (range nights fill it from the host). */
  required: "always" | "gun_show" | "one_of_org" | "no";
  help: string;
}

/** The template's columns, in order. The header row of the template is exactly these keys. */
export const EVENT_CSV_COLUMNS: EventCsvColumn[] = [
  {
    key: "event_type",
    required: "always",
    help: "gun_show or range_night",
  },
  { key: "title", required: "always", help: "The event name" },
  {
    key: "start_date",
    required: "always",
    help: "First day, YYYY-MM-DD (1/31/2027 also accepted)",
  },
  {
    key: "end_date",
    required: "no",
    help: "Last day of a gun show; leave blank for a range night (blank = same day)",
  },
  {
    key: "start_time",
    required: "always",
    help: "Local venue time the doors open on the first day: 09:00, 9:00 AM, 4pm",
  },
  {
    key: "end_time",
    required: "always",
    help: "Local venue time it closes on the last day: 16:00, 4:00 PM",
  },
  {
    key: "timezone",
    required: "no",
    help: "Blank = worked out from the venue ZIP (not the state; no ZIP = fill this in). Eastern, Central, Mountain, Arizona, Pacific, Alaska or Hawaii",
  },
  {
    key: "organization_facility_id",
    required: "one_of_org",
    help: "Range night: the host range / store. Gun show: the promoter. Give the facility id, the affiliate id or the exact name",
  },
  {
    key: "organization_affiliate_id",
    required: "one_of_org",
    help: "Alternative to the facility id",
  },
  {
    key: "organization_name",
    required: "one_of_org",
    help: "Alternative to the facility id: the organization's exact name in the CRM",
  },
  {
    key: "venue_name",
    required: "gun_show",
    help: "Gun shows: the show venue. Range nights: blank = the host's name",
  },
  {
    key: "venue_address",
    required: "gun_show",
    help: "Street address. Range nights: leave the venue columns blank to use the host's address",
  },
  { key: "venue_city", required: "gun_show", help: "City" },
  {
    key: "venue_state",
    required: "gun_show",
    help: "Two-letter state, e.g. TX",
  },
  { key: "venue_zip", required: "gun_show", help: "5-digit ZIP" },
  {
    key: "regulatory_state",
    required: "no",
    help: "Blank = the venue state. Only when the event's regulatory state differs from the venue",
  },
  {
    key: "max_attendees",
    required: "no",
    help: "Blank = 300 for a gun show, no limit for a range night",
  },
  {
    key: "entry_fee",
    required: "no",
    help: "Gun shows only: the entry fee (internal reimbursement tracking), e.g. 12",
  },
  {
    key: "description",
    required: "no",
    help: "Optional notes shown on the event",
  },
];

export const EVENT_CSV_HEADERS = EVENT_CSV_COLUMNS.map((c) => c.key);
export const EVENT_CSV_MAX_ROWS = 500;

/** Header spellings people actually use, mapped to the template key. */
const HEADER_ALIASES: Record<string, string> = {
  type: "event_type",
  eventtype: "event_type",
  name: "title",
  event_name: "title",
  event_title: "title",
  date: "start_date",
  start: "start_date",
  end: "end_date",
  open_time: "start_time",
  close_time: "end_time",
  time_zone: "timezone",
  tz: "timezone",
  facility_id: "organization_facility_id",
  org_facility_id: "organization_facility_id",
  affiliate_id: "organization_affiliate_id",
  org_affiliate_id: "organization_affiliate_id",
  organization: "organization_name",
  org_name: "organization_name",
  promoter: "organization_name",
  promoter_name: "organization_name",
  host: "organization_name",
  venue: "venue_name",
  location_name: "venue_name",
  address: "venue_address",
  address_line1: "venue_address",
  city: "venue_city",
  state: "venue_state",
  zip: "venue_zip",
  zip_code: "venue_zip",
  postal_code: "venue_zip",
  venue_postal_code: "venue_zip",
  capacity: "max_attendees",
  fee: "entry_fee",
};

export function normalizeHeader(h: string): string {
  const k = h
    .replace(/^\uFEFF/, "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  return HEADER_ALIASES[k] ?? k;
}

// ---------------------------------------------------------------------------
// Value normalisers. Each returns { value } or { error } (and sometimes a warning).
// ---------------------------------------------------------------------------
type Norm = { value: string | null; error?: string; warning?: string };

export function normalizeEventType(s: string): Norm {
  const k = s
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");
  if (k === "") {
    return { value: null, error: "required" };
  }
  if (k === "gunshow") {
    return { value: "gun_show" };
  }
  if (k === "rangenight") {
    return { value: "range_night" };
  }
  return { value: null, error: `"${s.trim()}" is not gun_show or range_night` };
}

function realDate(y: number, m: number, d: number): string | null {
  const dt = new Date(Date.UTC(y, m - 1, d));
  if (
    dt.getUTCFullYear() !== y ||
    dt.getUTCMonth() !== m - 1 ||
    dt.getUTCDate() !== d
  ) {
    return null;
  }
  return `${String(y).padStart(4, "0")}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

/** YYYY-MM-DD (also Y-M-D unpadded) or US M/D/YYYY (what Excel writes back). Two-digit years are refused. */
export function normalizeDate(s: string): Norm {
  const t = s.trim();
  if (t === "") {
    return { value: null };
  }
  let m = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(t);
  if (m) {
    const v = realDate(+m[1], +m[2], +m[3]);
    return v
      ? { value: v }
      : { value: null, error: `"${t}" is not a real date` };
  }
  m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(t);
  if (m) {
    const v = realDate(+m[3], +m[1], +m[2]);
    return v
      ? { value: v }
      : { value: null, error: `"${t}" is not a real date` };
  }
  if (/^\d{1,2}\/\d{1,2}\/\d{2}$/.test(t)) {
    return { value: null, error: `"${t}": use a 4-digit year (YYYY-MM-DD)` };
  }
  return { value: null, error: `"${t}" is not a date (use YYYY-MM-DD)` };
}

/** 24-hour HH:MM[:SS], or 12-hour "9am", "9:30 PM", "12 noon". A bare number is refused (AM or PM?). */
export function normalizeTime(s: string): Norm {
  const t = s.trim().toLowerCase().replace(/\s+/g, " ");
  if (t === "") {
    return { value: null };
  }
  if (t === "noon" || t === "12 noon") {
    return { value: "12:00" };
  }
  let m = /^(\d{1,2})(?::(\d{2}))?\s*(a|p)\.?\s*m?\.?$/.exec(t);
  if (m) {
    let h = +m[1];
    const min = m[2] ? +m[2] : 0;
    if (h < 1 || h > 12 || min > 59) {
      return { value: null, error: `"${s.trim()}" is not a time` };
    }
    if (m[3] === "a") {
      h = h === 12 ? 0 : h;
    } else {
      h = h === 12 ? 12 : h + 12;
    }
    return {
      value: `${String(h).padStart(2, "0")}:${String(min).padStart(2, "0")}`,
    };
  }
  m = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(t);
  if (m) {
    const h = +m[1];
    const min = +m[2];
    if (h > 23 || min > 59) {
      return { value: null, error: `"${s.trim()}" is not a time` };
    }
    return {
      value: `${String(h).padStart(2, "0")}:${String(min).padStart(2, "0")}`,
    };
  }
  if (/^\d{1,2}$/.test(t)) {
    return {
      value: null,
      error: `"${s.trim()}": add AM or PM, or use 24-hour time like 16:00`,
    };
  }
  return {
    value: null,
    error: `"${s.trim()}" is not a time (use 09:00 or 9:00 AM)`,
  };
}

const TZ_ALIASES: Record<string, string> = {
  eastern: "America/New_York",
  et: "America/New_York",
  est: "America/New_York",
  edt: "America/New_York",
  "america/new_york": "America/New_York",
  "america/detroit": "America/New_York",
  "america/indiana/indianapolis": "America/New_York",
  "america/kentucky/louisville": "America/New_York",
  central: "America/Chicago",
  ct: "America/Chicago",
  cst: "America/Chicago",
  cdt: "America/Chicago",
  "america/chicago": "America/Chicago",
  mountain: "America/Denver",
  mt: "America/Denver",
  mdt: "America/Denver",
  "america/denver": "America/Denver",
  "america/boise": "America/Denver",
  arizona: "America/Phoenix",
  mst: "America/Phoenix",
  "america/phoenix": "America/Phoenix",
  pacific: "America/Los_Angeles",
  pt: "America/Los_Angeles",
  pst: "America/Los_Angeles",
  pdt: "America/Los_Angeles",
  "america/los_angeles": "America/Los_Angeles",
  alaska: "America/Anchorage",
  akst: "America/Anchorage",
  "america/anchorage": "America/Anchorage",
  hawaii: "Pacific/Honolulu",
  hst: "Pacific/Honolulu",
  "pacific/honolulu": "Pacific/Honolulu",
};

export function normalizeTimezone(s: string): Norm {
  const t = s
    .trim()
    .toLowerCase()
    .replace(/\s*\(.*\)$/, "")
    .replace(/\s+time$/, "");
  if (t === "") {
    return { value: null };
  }
  const v = TZ_ALIASES[t];
  return v
    ? { value: v }
    : {
        value: null,
        error: `"${s.trim()}" is not a US event time zone (Eastern, Central, Mountain, Arizona, Pacific, Alaska, Hawaii)`,
      };
}

const STATE_NAMES: Record<string, string> = {
  alabama: "AL",
  alaska: "AK",
  arizona: "AZ",
  arkansas: "AR",
  california: "CA",
  colorado: "CO",
  connecticut: "CT",
  delaware: "DE",
  "district of columbia": "DC",
  florida: "FL",
  georgia: "GA",
  hawaii: "HI",
  idaho: "ID",
  illinois: "IL",
  indiana: "IN",
  iowa: "IA",
  kansas: "KS",
  kentucky: "KY",
  louisiana: "LA",
  maine: "ME",
  maryland: "MD",
  massachusetts: "MA",
  michigan: "MI",
  minnesota: "MN",
  mississippi: "MS",
  missouri: "MO",
  montana: "MT",
  nebraska: "NE",
  nevada: "NV",
  "new hampshire": "NH",
  "new jersey": "NJ",
  "new mexico": "NM",
  "new york": "NY",
  "north carolina": "NC",
  "north dakota": "ND",
  ohio: "OH",
  oklahoma: "OK",
  oregon: "OR",
  pennsylvania: "PA",
  "rhode island": "RI",
  "south carolina": "SC",
  "south dakota": "SD",
  tennessee: "TN",
  texas: "TX",
  utah: "UT",
  vermont: "VT",
  virginia: "VA",
  washington: "WA",
  "west virginia": "WV",
  wisconsin: "WI",
  wyoming: "WY",
  "puerto rico": "PR",
};
const STATE_CODES = new Set(Object.values(STATE_NAMES));

export function normalizeState(s: string): Norm {
  const t = s.trim();
  if (t === "") {
    return { value: null };
  }
  const up = t.toUpperCase().replace(/\./g, "");
  if (STATE_CODES.has(up)) {
    return { value: up };
  }
  const byName = STATE_NAMES[t.toLowerCase().replace(/\s+/g, " ")];
  return byName
    ? { value: byName }
    : { value: null, error: `"${t}" is not a US state` };
}

/** 5 digits or ZIP+4 -> 5 digits. 3-4 digits = Excel dropped the leading zeros (02134 -> 2134): restored, with a warning. */
export function normalizeZip(s: string): Norm {
  const t = s.trim();
  if (t === "") {
    return { value: null };
  }
  const z5 = normalizeZip5(t);
  if (z5) {
    return { value: z5 };
  }
  if (/^\d{3,4}$/.test(t)) {
    const v = t.padStart(5, "0");
    return {
      value: v,
      warning: `ZIP "${t}" read as ${v} (spreadsheet dropped the leading zero)`,
    };
  }
  return { value: null, error: `"${t}" is not a 5-digit ZIP` };
}

export function normalizeWholeNumber(s: string): Norm {
  const t = s.trim().replace(/,/g, "");
  if (t === "") {
    return { value: null };
  }
  if (!/^\d+$/.test(t) || +t < 1 || +t > 999999) {
    return {
      value: null,
      error: `"${s.trim()}" must be a whole number of 1 or more`,
    };
  }
  return { value: String(+t) };
}

export function normalizeMoney(s: string): Norm {
  const t = s.trim().replace(/^\$/, "").replace(/,/g, "").trim();
  if (t === "") {
    return { value: null };
  }
  if (!/^\d{1,5}(\.\d{1,2})?$/.test(t)) {
    return {
      value: null,
      error: `"${s.trim()}" must be an amount like 12 or 12.50`,
    };
  }
  return { value: t };
}

// ---------------------------------------------------------------------------
// Rows
// ---------------------------------------------------------------------------

/** The strict shape the database plan / create functions take (every value a string or null). */
export interface NormalizedEventRow {
  event_type: string | null;
  title: string | null;
  description: string | null;
  start_date: string | null;
  end_date: string | null;
  start_time: string | null;
  end_time: string | null;
  timezone: string | null;
  /**
   * Set when the browser WORKED OUT the time zone (the row left it blank): the venue ZIP / state it used. The database
   * refuses the row if the venue it resolves is not that venue, so the zone always belongs to the event's venue.
   */
  timezone_derived: boolean;
  timezone_from_zip: string | null;
  timezone_from_state: string | null;
  org_facility_id: string | null;
  org_affiliate_id: string | null;
  org_name: string | null;
  venue_name: string | null;
  venue_address: string | null;
  venue_city: string | null;
  venue_state: string | null;
  venue_zip: string | null;
  regulatory_state: string | null;
  max_attendees: string | null;
  entry_fee: string | null;
}

export interface ParsedEventRow {
  /** Spreadsheet row number (the header is row 1). */
  rowNumber: number;
  raw: Record<string, string>;
  row: NormalizedEventRow;
  /** "column: message" */
  errors: string[];
  warnings: string[];
  /** Where the time zone came from when the row left it blank. */
  timezoneSource: "row" | "zip" | "state" | null;
}

export interface ParsedEventCsv {
  rows: ParsedEventRow[];
  /** Problems with the file as a whole (no rows, missing columns, too many rows). */
  fileErrors: string[];
  /** Columns in the file that the import does not use (ignored). */
  unknownHeaders: string[];
}

const blankToNull = (s: string | undefined): string | null => {
  const t = (s ?? "").trim();
  return t === "" ? null : t;
};

export function normalizeEventRow(
  raw: Record<string, string>,
  rowNumber: number,
): ParsedEventRow {
  const errors: string[] = [];
  const warnings: string[] = [];
  const take = (key: string, n: Norm): string | null => {
    if (n.error) {
      errors.push(`${key}: ${n.error}`);
    }
    if (n.warning) {
      warnings.push(`${key}: ${n.warning}`);
    }
    return n.value;
  };
  const g = (k: string) => raw[k] ?? "";

  const type = take("event_type", normalizeEventType(g("event_type")));
  const title = blankToNull(g("title"));
  if (!title) {
    errors.push("title: required");
  } else if (title.length > 200) {
    errors.push("title: longer than 200 characters");
  }
  const startDate = take("start_date", normalizeDate(g("start_date")));
  if (!startDate && !errors.some((e) => e.startsWith("start_date"))) {
    errors.push("start_date: required");
  }
  let endDate = take("end_date", normalizeDate(g("end_date")));
  if (type === "range_night" && endDate && startDate && endDate !== startDate) {
    errors.push(
      "end_date: a range night is a single day (leave end_date blank)",
    );
  }
  if (startDate && endDate && endDate < startDate) {
    errors.push("end_date: before start_date");
  }
  if (!endDate) {
    endDate = startDate;
  }
  const startTime = take("start_time", normalizeTime(g("start_time")));
  if (!startTime && !errors.some((e) => e.startsWith("start_time"))) {
    errors.push("start_time: required");
  }
  const endTime = take("end_time", normalizeTime(g("end_time")));
  if (!endTime && !errors.some((e) => e.startsWith("end_time"))) {
    errors.push("end_time: required");
  }
  if (
    startDate &&
    endDate === startDate &&
    startTime &&
    endTime &&
    endTime <= startTime
  ) {
    errors.push("end_time: must be after start_time on a one-day event");
  }

  let timezone = take("timezone", normalizeTimezone(g("timezone")));
  const orgFacility = blankToNull(g("organization_facility_id"));
  const orgAffiliate = blankToNull(g("organization_affiliate_id"));
  const orgName = blankToNull(g("organization_name"));
  if (!orgFacility && !orgAffiliate && !orgName) {
    errors.push(
      type === "gun_show"
        ? "organization: give the promoter's organization_facility_id, organization_affiliate_id or organization_name"
        : "organization: give the host's organization_facility_id, organization_affiliate_id or organization_name",
    );
  }

  const venueName = blankToNull(g("venue_name"));
  const venueAddress = blankToNull(g("venue_address"));
  const venueCity = blankToNull(g("venue_city"));
  const venueState = take("venue_state", normalizeState(g("venue_state")));
  const venueZip = take("venue_zip", normalizeZip(g("venue_zip")));
  if (type === "gun_show") {
    if (!venueName) {
      errors.push("venue_name: required for a gun show");
    }
    if (!venueAddress) {
      errors.push("venue_address: required for a gun show");
    }
    if (!venueCity) {
      errors.push("venue_city: required for a gun show");
    }
    if (!venueState && !errors.some((e) => e.startsWith("venue_state"))) {
      errors.push("venue_state: required for a gun show");
    }
    if (!venueZip && !errors.some((e) => e.startsWith("venue_zip"))) {
      errors.push("venue_zip: required for a gun show");
    }
  }
  const regulatoryState = take(
    "regulatory_state",
    normalizeState(g("regulatory_state")),
  );
  const maxAttendees = take(
    "max_attendees",
    normalizeWholeNumber(g("max_attendees")),
  );
  const entryFee = take("entry_fee", normalizeMoney(g("entry_fee")));
  if (type === "range_night" && entryFee) {
    errors.push(
      "entry_fee: only gun shows have an entry fee (leave it blank for a range night)",
    );
  }
  const description = blankToNull(g("description"));

  let timezoneSource: ParsedEventRow["timezoneSource"] = timezone
    ? "row"
    : null;
  let timezoneDerived = false;
  // EVT-08b (owner 2026-10-30): a blank zone is worked out from the venue ZIP ONLY, never the
  // state. timezone_from_state still records the venue state: the database compares both to the
  // venue it resolves (provenance, audit F2). No ZIP -> no zone -> the "add a timezone" error.
  if (!timezone && venueZip) {
    const resolved = resolveEventTimezoneByZip(venueZip);
    if (resolved) {
      timezone = resolved.timezone;
      timezoneSource = resolved.source;
      timezoneDerived = true;
    }
  }

  return {
    rowNumber,
    raw,
    errors,
    warnings,
    timezoneSource,
    row: {
      event_type: type,
      title,
      description,
      start_date: startDate,
      end_date: endDate,
      start_time: startTime,
      end_time: endTime,
      timezone,
      timezone_derived: timezoneDerived,
      timezone_from_zip: timezoneDerived ? venueZip : null,
      timezone_from_state: timezoneDerived ? venueState : null,
      org_facility_id: orgFacility,
      org_affiliate_id: orgAffiliate,
      org_name: orgName,
      venue_name: venueName,
      venue_address: venueAddress,
      venue_city: venueCity,
      venue_state: venueState,
      venue_zip: venueZip,
      regulatory_state: regulatoryState,
      max_attendees: maxAttendees,
      entry_fee: entryFee,
    },
  };
}

/** Parse the CSV text: header row + data rows. Blank rows are skipped; row numbers follow the spreadsheet. */
export function parseEventCsv(text: string): ParsedEventCsv {
  const fileErrors: string[] = [];
  const parsed = Papa.parse<string[]>(text.replace(/^\uFEFF/, ""), {
    header: false,
    skipEmptyLines: false,
  });
  const lines = parsed.data;
  // First non-blank line is the header.
  let h = 0;
  while (h < lines.length && lines[h].every((c) => (c ?? "").trim() === "")) {
    h++;
  }
  if (h >= lines.length) {
    return { rows: [], fileErrors: ["The file is empty."], unknownHeaders: [] };
  }
  const headers = lines[h].map(normalizeHeader);
  const known = new Set(EVENT_CSV_HEADERS);
  const unknownHeaders = lines[h].filter(
    (_, i) => headers[i] && !known.has(headers[i]),
  );
  const missing = [
    "event_type",
    "title",
    "start_date",
    "start_time",
    "end_time",
  ].filter((k) => !headers.includes(k));
  if (missing.length) {
    fileErrors.push(
      `Missing column${missing.length > 1 ? "s" : ""}: ${missing.join(", ")}. Start from the template so the header row is right.`,
    );
  }
  if (
    ![
      "organization_facility_id",
      "organization_affiliate_id",
      "organization_name",
    ].some((k) => headers.includes(k))
  ) {
    fileErrors.push(
      "Missing an organization column: add organization_facility_id, organization_affiliate_id or organization_name.",
    );
  }
  const dupHeaders = headers.filter(
    (k, i) => k && known.has(k) && headers.indexOf(k) !== i,
  );
  if (dupHeaders.length) {
    fileErrors.push(
      `Column given twice: ${[...new Set(dupHeaders)].join(", ")}.`,
    );
  }

  const rows: ParsedEventRow[] = [];
  for (let i = h + 1; i < lines.length; i++) {
    const cells = lines[i];
    if (cells.every((c) => (c ?? "").trim() === "")) {
      continue;
    }
    const raw: Record<string, string> = {};
    headers.forEach((k, j) => {
      if (k && known.has(k) && raw[k] === undefined) {
        raw[k] = (cells[j] ?? "").trim();
      }
    });
    rows.push(normalizeEventRow(raw, i + 1));
  }
  if (rows.length === 0 && fileErrors.length === 0) {
    fileErrors.push("The file has a header row but no events.");
  }
  if (rows.length > EVENT_CSV_MAX_ROWS) {
    fileErrors.push(
      `At most ${EVENT_CSV_MAX_ROWS} events per file (this file has ${rows.length}). Split it.`,
    );
  }
  return { rows, fileErrors, unknownHeaders };
}

// ---------------------------------------------------------------------------
// Template + example files
// ---------------------------------------------------------------------------
const csvCell = (v: string): string =>
  /[",\n\r]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
const csvLine = (cells: string[]): string => cells.map(csvCell).join(",");

export const EVENT_CSV_EXAMPLE_ROWS: Record<string, string>[] = [
  {
    event_type: "gun_show",
    title: "C&E Gun Show - Raleigh",
    start_date: "2027-01-09",
    end_date: "2027-01-10",
    start_time: "09:00",
    end_time: "17:00",
    organization_facility_id: "15127",
    venue_name: "NC State Fairgrounds",
    venue_address: "4285 Trinity Road",
    venue_city: "Raleigh",
    venue_state: "NC",
    venue_zip: "27607",
    entry_fee: "12",
  },
  {
    event_type: "gun_show",
    title: "Big Boss Gun Shows - Live Oak",
    start_date: "2027-01-15",
    end_date: "2027-01-17",
    start_time: "4:00 PM",
    end_time: "4:00 PM",
    organization_name: "Big Boss Gun Show",
    venue_name: "Hilton Garden Inn Live Oak Conference Center",
    venue_address: "8101 Pat Booker Rd",
    venue_city: "Live Oak",
    venue_state: "TX",
    venue_zip: "78233",
    max_attendees: "300",
    entry_fee: "10",
  },
  {
    event_type: "range_night",
    title: "Caliber Gun Range - Range Night",
    start_date: "2027-01-21",
    start_time: "16:00",
    end_time: "20:00",
    timezone: "Central",
    organization_name: "Caliber Gun Range",
    max_attendees: "50",
  },
];

export function eventCsvTemplate(): string {
  return csvLine(EVENT_CSV_HEADERS) + "\r\n";
}

export function eventCsvExample(): string {
  return (
    [
      csvLine(EVENT_CSV_HEADERS),
      ...EVENT_CSV_EXAMPLE_ROWS.map((r) =>
        csvLine(EVENT_CSV_HEADERS.map((k) => r[k] ?? "")),
      ),
    ].join("\r\n") + "\r\n"
  );
}

export function toCsv(header: string[], rows: string[][]): string {
  return [csvLine(header), ...rows.map(csvLine)].join("\r\n") + "\r\n";
}

/**
 * Token binding a preview to the file name and the exact rows it planned (row number, status, server plan digest and
 * the row the browser will send). Create recomputes it and refuses if anything differs (audit 73915ddd F1). Not a
 * security boundary — the database re-checks every row against its plan digest — it stops a stale or replaced preview
 * from being acted on.
 */
export function previewToken(
  gen: number,
  fileName: string,
  rows: Array<{
    parsed: { rowNumber: number };
    status: string;
    plan: { plan_digest?: string } | null;
    sent: unknown;
  }>,
): string {
  const text = JSON.stringify([
    gen,
    fileName,
    rows.map((r) => [
      r.parsed.rowNumber,
      r.status,
      r.plan?.plan_digest ?? null,
      r.sent,
    ]),
  ]);
  let h1 = 0x811c9dc5;
  let h2 = 0x01000193;
  for (let i = 0; i < text.length; i++) {
    const c = text.charCodeAt(i);
    h1 = Math.imul(h1 ^ c, 0x01000193) >>> 0;
    h2 = Math.imul(h2 ^ c, 0x85ebca6b) >>> 0;
  }
  return `${gen}:${text.length}:${h1.toString(16)}${h2.toString(16)}`;
}
